import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { AgeCategory, YouthChildrenSubCategory } from '../../types/camper';
import StayHistoryCard from './RegistrationDetails/StayHistoryCard';

type PrimaryMode = 'existing' | 'new';

interface PrimaryCamperOption {
  id: string;
  FirstName: string;
  LastName: string;
  email: string;
  HomePhone: string;
  MobilePhone?: string;
  par?: number;
  dependants?: Array<{ FirstName?: string; LastName?: string; Age?: AgeCategory; YouthChildrenCategory?: YouthChildrenSubCategory }>;
  deleted?: boolean;
}

interface AccommodationOption {
  id: string;
  placeCode: string;
  placeName: string;
  type: string;
  priceCode?: string;
}

interface AttendingCamper {
  firstName: string;
  lastName: string;
  age: AgeCategory;
  youthChildrenCategory?: YouthChildrenSubCategory;
  nights: number;
  isPrimary?: boolean;
  selected?: boolean;
  programInterests?: {
    nurseryProgram: boolean;
    vbsProgram: boolean;
    youthGroup: boolean;
    cruiseProgram: boolean;
    volunteerInterest: boolean;
  };
}

interface AssignedAccommodationSnapshot {
  placeCode: string;
  placeName: string;
  type: string;
  priceCode: string;
}

const calculateNights = (arrivalDate: string, departureDate: string): number => {
  if (!arrivalDate || !departureDate) return 0;
  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);
  const diff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return nights > 0 ? nights : 0;
};

const parseDateValue = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  if (typeof dateValue?.toDate === 'function') return dateValue.toDate();
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'string') {
    const datePart = dateValue.split('T')[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
    }
  }
  const parsed = new Date(dateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toInputDateString = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDateByDays = (dateValue: any, days: number): string => {
  const parsed = parseDateValue(dateValue);
  if (!parsed) return '';
  const shifted = new Date(parsed);
  shifted.setDate(shifted.getDate() + days);
  return toInputDateString(shifted);
};

const formatCampDate = (dateValue: any): string => {
  const parsed = parseDateValue(dateValue);
  if (!parsed) return 'Not set';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatReadableDate = (dateValue: string): string => {
  const parsed = parseDateValue(dateValue);
  if (!parsed) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const getNightsDisplayText = (nights: number): string => {
  if (nights >= 5) return 'Full Camp';
  if (nights === 1) return '1 Night';
  return `${nights} Nights`;
};

const toAgeCategory = (value: any): AgeCategory => {
  if (value === AgeCategory.AdultStudent) return AgeCategory.AdultStudent;
  if (value === AgeCategory.YouthChildren) return AgeCategory.YouthChildren;
  return AgeCategory.Adult;
};

const toYouthChildrenCategory = (value: any): YouthChildrenSubCategory | undefined => {
  if (
    value === YouthChildrenSubCategory.Nursery ||
    value === YouthChildrenSubCategory.PreSchool ||
    value === YouthChildrenSubCategory.GradeP5 ||
    value === YouthChildrenSubCategory.Grade68 ||
    value === YouthChildrenSubCategory.Grade912
  ) {
    return value;
  }
  return undefined;
};

const emptyProgramInterests = () => ({
  nurseryProgram: false,
  vbsProgram: false,
  youthGroup: false,
  cruiseProgram: false,
  volunteerInterest: false
});

const calculateCamperRegistrationFee = (camper: AttendingCamper, registrationRates: any): number => {
  const camperNights = camper.nights || 0;
  const isFullCamp = camperNights >= 5;

  if (camper.age === AgeCategory.AdultStudent) {
    return isFullCamp
      ? (registrationRates?.adultStudent?.weekly || 0)
      : (registrationRates?.adultStudent?.nightly || 0) * camperNights;
  }

  if (camper.age === AgeCategory.YouthChildren) {
    return isFullCamp
      ? (registrationRates?.youthAndChildren?.weekly || 0)
      : (registrationRates?.youthAndChildren?.nightly || 0) * camperNights;
  }

  return isFullCamp
    ? (registrationRates?.adult?.weekly || 0)
    : (registrationRates?.adult?.nightly || 0) * camperNights;
};

const calculateAccommodationFeeEstimate = (
  accommodationRates: any,
  placeType: string,
  priceCode: string | undefined,
  nights: number
): number => {
  if (!accommodationRates || !priceCode || nights <= 0) return 0;

  const isFullCamp = nights >= 5;
  const normalizedPriceCode = String(priceCode).toUpperCase();

  // Resolve the rate by price code (matches the regular assignment flow).
  // Dorm and Fireside share the D1/D2 rates, which live in the dorm bucket.
  const dormRates = accommodationRates.dorm;
  if (normalizedPriceCode.startsWith('D') && dormRates?.[normalizedPriceCode]) {
    const rate = dormRates[normalizedPriceCode];
    return isFullCamp ? (rate.weekly || 0) : (rate.nightly || 0) * nights;
  }

  // Otherwise find the price code in whichever rate bucket contains it.
  const accommodationTypes = ['apartments', 'campsite', 'cottages', 'fireside', 'lowerCourt', 'upperCourt', 'dormA', 'dormB', 'privateCottage'];
  for (const type of accommodationTypes) {
    const bucketRates = accommodationRates[type];
    if (bucketRates?.[normalizedPriceCode]) {
      const rate = bucketRates[normalizedPriceCode];
      return isFullCamp ? (rate.weekly || 0) : (rate.nightly || 0) * nights;
    }
  }

  return 0;
};

const ManualRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const manualRegistrationPath = '/admin/assignments/manual-registration';
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [campSettings, setCampSettings] = useState<any>(null);
  const [existingCampers, setExistingCampers] = useState<PrimaryCamperOption[]>([]);
  const [accommodations, setAccommodations] = useState<AccommodationOption[]>([]);

  const [primaryMode, setPrimaryMode] = useState<PrimaryMode>('existing');
  const [camperSearch, setCamperSearch] = useState('');
  const [selectedExistingCamperId, setSelectedExistingCamperId] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [primaryPhoneAtCamp, setPrimaryPhoneAtCamp] = useState('');
  const [registrationType, setRegistrationType] = useState('new-camper');
  const [registrationStatus, setRegistrationStatus] = useState('Pending');

  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [assignedAccommodationCode, setAssignedAccommodationCode] = useState('');
  const [assignedAccommodation, setAssignedAccommodation] = useState<AssignedAccommodationSnapshot | null>(null);
  const [accommodationAssignmentInput, setAccommodationAssignmentInput] = useState('');
  const [accommodationNotes, setAccommodationNotes] = useState('');

  const [attendingCampers, setAttendingCampers] = useState<AttendingCamper[]>([]);
  const [showOverrides, setShowOverrides] = useState({
    accommodation: false,
    registration: false,
    mealFees: false,
    otherFees: false
  });
  const [overrides, setOverrides] = useState({
    accommodation: { amount: 0, notes: '' },
    registration: { amount: 0, notes: '' },
    mealFees: { amount: 0, notes: '' },
    otherFees: { amount: 0, notes: '' }
  });
  const [fridgeRentalRequested, setFridgeRentalRequested] = useState(false);
  const [donationEnabled, setDonationEnabled] = useState(false);
  const [donationAmount, setDonationAmount] = useState(0);
  const [otherFeesEnabled, setOtherFeesEnabled] = useState(false);
  const [otherFeesAmount, setOtherFeesAmount] = useState(0);
  const [otherFeesNote, setOtherFeesNote] = useState('');
  const [parBalance, setParBalance] = useState(0);
  const [manualRegistrationId, setManualRegistrationId] = useState<string | null>(null);
  const [draftInitialized, setDraftInitialized] = useState(false);
  const skipNextCamperAutoPopulateRef = useRef(false);

  const selectedExistingCamper = useMemo(
    () => existingCampers.find((camper) => camper.id === selectedExistingCamperId) || null,
    [existingCampers, selectedExistingCamperId]
  );

  const filteredCampers = useMemo(() => {
    const q = camperSearch.trim().toLowerCase();
    if (!q) return existingCampers;

    return existingCampers.filter((c) => {
      const fullName = `${c.FirstName} ${c.LastName}`.toLowerCase();
      if (fullName.includes(q) || (c.email || '').toLowerCase().includes(q)) {
        return true;
      }

      return c.dependants?.some((dep) =>
        (dep.FirstName || '').toLowerCase().includes(q) ||
        (dep.LastName || '').toLowerCase().includes(q)
      ) || false;
    });
  }, [camperSearch, existingCampers]);

  const selectedAccommodation = useMemo(() => {
    const normalizedCode = String(assignedAccommodationCode || '').trim().toLowerCase();
    if (!normalizedCode) return null;
    return accommodations.find(
      (accommodation) => String(accommodation.placeCode || '').trim().toLowerCase() === normalizedCode
    ) || null;
  }, [accommodations, assignedAccommodationCode]);
  const primaryDisplayName = selectedExistingCamper
    ? `${selectedExistingCamper.FirstName || ''} ${selectedExistingCamper.LastName || ''}`.trim()
    : `${newFirstName || ''} ${newLastName || ''}`.trim();
  const stayHistoryRegistration = useMemo(
    () =>
      ({
        id: 'manual-registration',
        registrationInfo: {
          email: primaryEmail || ''
        }
      } as any),
    [primaryEmail]
  );

  const nights = calculateNights(arrivalDate, departureDate);
  const campStartMinDate = shiftDateByDays(campSettings?.startDate, -1);
  const campEndMaxDate = shiftDateByDays(campSettings?.endDate, 1);
  const campStartDate = toInputDateString(parseDateValue(campSettings?.startDate));
  const campEndDate = toInputDateString(parseDateValue(campSettings?.endDate));
  const campDateRange = `${formatCampDate(campSettings?.startDate)} - ${formatCampDate(campSettings?.endDate)}`;
  const selectedCampers = useMemo(
    () => attendingCampers.filter((camper) => camper.selected),
    [attendingCampers]
  );
  const buildAttendingCampersForExistingCamper = (camper: PrimaryCamperOption, baseNights: number): AttendingCamper[] => {
    const dependantCampers = (camper.dependants || []).map((dependant: any) => ({
      firstName: dependant.FirstName || dependant.firstName || '',
      lastName: dependant.LastName || dependant.lastName || '',
      age: toAgeCategory(dependant.Age ?? dependant.age),
      youthChildrenCategory: toYouthChildrenCategory(
        dependant.YouthChildrenCategory ?? dependant.youthChildrenCategory ?? dependant.youthChildrenSubCategory
      ),
      nights: baseNights,
      selected: false,
      programInterests: emptyProgramInterests(),
      isPrimary: false
    })).filter((dependant) => dependant.firstName || dependant.lastName);

    return [
      {
        firstName: camper.FirstName || '',
        lastName: camper.LastName || '',
        age: AgeCategory.Adult,
        nights: baseNights,
        selected: true,
        programInterests: emptyProgramInterests(),
        isPrimary: true
      },
      ...dependantCampers
    ];
  };
  const registrationFeeSummary = useMemo(() => {
    const registrationRates = campSettings?.rates?.registration || {};
    const adults: Array<{ name: string; fee: number; nights: number }> = [];
    const adultStudents: Array<{ name: string; fee: number; nights: number }> = [];
    const youthChildren: Array<{ name: string; fee: number; nights: number }> = [];
    let totalRegistrationFees = 0;

    selectedCampers.forEach((camper) => {
      const fee = calculateCamperRegistrationFee(camper, registrationRates);
      const row = {
        name: `${camper.firstName} ${camper.lastName}`.trim(),
        fee,
        nights: camper.nights || 0
      };

      if (camper.age === AgeCategory.AdultStudent) {
        adultStudents.push(row);
      } else if (camper.age === AgeCategory.YouthChildren) {
        youthChildren.push(row);
      } else {
        adults.push(row);
      }

      totalRegistrationFees += fee;
    });

    return {
      adults,
      adultStudents,
      youthChildren,
      totalRegistrationFees
    };
  }, [selectedCampers, campSettings]);
  const accommodationFeeEstimate = useMemo(
    () => calculateAccommodationFeeEstimate(
      campSettings?.rates?.accommodation,
      assignedAccommodation?.type || selectedAccommodation?.type || '',
      assignedAccommodation?.priceCode || selectedAccommodation?.priceCode,
      nights
    ),
    [campSettings, assignedAccommodation, selectedAccommodation, nights]
  );
  const accommodationSubtotal = accommodationFeeEstimate + (showOverrides.accommodation ? overrides.accommodation.amount : 0);
  const registrationSubtotal = registrationFeeSummary.totalRegistrationFees + (showOverrides.registration ? overrides.registration.amount : 0);
  const mealBaseSubtotal = 0;
  const mealFeesSubtotal = mealBaseSubtotal + (showOverrides.mealFees ? overrides.mealFees.amount : 0);
  const fridgeCost = fridgeRentalRequested ? 25 : 0;
  const donationCost = donationEnabled ? donationAmount : 0;
  const customOtherFeesCost = otherFeesEnabled ? otherFeesAmount : 0;
  const otherBaseSubtotal = fridgeCost + donationCost + customOtherFeesCost;
  const otherFeesSubtotal = otherBaseSubtotal + (showOverrides.otherFees ? overrides.otherFees.amount : 0);
  const estimatedGrandTotal =
    accommodationSubtotal +
    registrationSubtotal +
    mealFeesSubtotal +
    otherFeesSubtotal;
  const totalPayments = 0;
  const remainingBalance = estimatedGrandTotal;
  useEffect(() => {
    const load = async () => {
      try {
        const [settingsSnap, campersSnap, inventorySnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'camp')),
          getDocs(query(collection(db, 'primaryCampers'), where('deleted', '==', false))),
          getDoc(doc(db, 'inventory', 'accommodations'))
        ]);

        if (settingsSnap.exists()) setCampSettings(settingsSnap.data());
        const loadedCampers = campersSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }) as PrimaryCamperOption)
          .sort((a, b) => (a.LastName || '').localeCompare(b.LastName || ''));
        setExistingCampers(loadedCampers);
        const flattenedAccommodations: AccommodationOption[] = [];
        if (inventorySnap.exists()) {
          const inventoryData = inventorySnap.data() as Record<string, any>;
          const inventoryTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];

          inventoryTypes.forEach((inventoryType) => {
            const entries = inventoryData[inventoryType];
            if (!Array.isArray(entries)) return;

            entries.forEach((entry: any, index: number) => {
              const normalizedType =
                inventoryType === 'dormA' || inventoryType === 'dormB'
                  ? 'Dorm'
                  : inventoryType === 'privateCottage'
                    ? 'Cottage'
                    : inventoryType;

              flattenedAccommodations.push({
                id: `${inventoryType}-${index}-${entry.placeCode || ''}`,
                placeCode: String(entry.placeCode || '').trim(),
                placeName: entry.placeName || entry.inventoryName || entry.placeCode || 'Unknown',
                type: normalizedType,
                priceCode: entry.priceCode || ''
              });
            });
          });
        }

        setAccommodations(flattenedAccommodations);
      } catch (error) {
        console.error('Failed to load manual registration page data:', error);
      } finally {
        setDraftInitialized(true);
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!campSettings) return;
    if (!arrivalDate && campStartDate) {
      setArrivalDate(campStartDate);
    }
    if (!departureDate && campEndDate) {
      setDepartureDate(campEndDate);
    }
  }, [campSettings, campStartDate, campEndDate, arrivalDate, departureDate]);

  useEffect(() => {
    if (loading || !draftInitialized || !campSettings || manualRegistrationId) return;

    const createManualDraftRegistration = async () => {
      try {
        const draftRef = await addDoc(collection(db, 'Registrations'), {
          status: 'Pending',
          year: campSettings?.year || new Date().getFullYear(),
          createdAt: serverTimestamp(),
          manualDraft: true
        });
        setManualRegistrationId(draftRef.id);
      } catch (error) {
        console.error('Failed to create manual registration draft:', error);
      }
    };

    createManualDraftRegistration();
  }, [loading, draftInitialized, campSettings, manualRegistrationId]);

  useEffect(() => {
    if (!selectedAccommodation) return;
    setAssignedAccommodation({
      placeCode: selectedAccommodation.placeCode,
      placeName: selectedAccommodation.placeName || '',
      type: selectedAccommodation.type || '',
      priceCode: selectedAccommodation.priceCode || ''
    });
  }, [selectedAccommodation]);

  useEffect(() => {
    if (primaryMode !== 'existing' || !selectedExistingCamper) return;
    setPrimaryEmail(selectedExistingCamper.email || '');
    setPrimaryPhoneAtCamp(selectedExistingCamper.HomePhone || selectedExistingCamper.MobilePhone || '');
  }, [primaryMode, selectedExistingCamper]);

  useEffect(() => {
    if (primaryMode === 'existing' && selectedExistingCamper) {
      setParBalance(Number(selectedExistingCamper.par || 0));
      return;
    }

    setParBalance(0);
  }, [primaryMode, selectedExistingCamper]);

  useEffect(() => {
    if (skipNextCamperAutoPopulateRef.current) {
      skipNextCamperAutoPopulateRef.current = false;
      return;
    }

    const baseNights = nights || 0;

    if (primaryMode === 'existing' && selectedExistingCamper) {
      setAttendingCampers(buildAttendingCampersForExistingCamper(selectedExistingCamper, baseNights));
      return;
    }

    if (primaryMode === 'new') {
      const hasName = newFirstName.trim() || newLastName.trim();
      setAttendingCampers(hasName ? [{
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        age: AgeCategory.Adult,
        nights: baseNights,
        selected: true,
        programInterests: emptyProgramInterests(),
        isPrimary: true
      }] : []);
    }
  }, [primaryMode, selectedExistingCamperId, newFirstName, newLastName]);

  useEffect(() => {
    const baseNights = nights || 0;
    setAttendingCampers((prev) =>
      prev.map((camper) => ({
        ...camper,
        nights: baseNights
      }))
    );
  }, [nights]);

  const handleAssignAccommodation = async () => {
    const normalizedCode = accommodationAssignmentInput.trim().toLowerCase();
    if (!normalizedCode) return;

    const match = accommodations.find(
      (accommodation) => String(accommodation.placeCode || '').trim().toLowerCase() === normalizedCode
    );

    if (!match) {
      alert(`Accommodation with place code "${accommodationAssignmentInput}" not found.`);
      return;
    }

    try {
      // Warn when this unit is already assigned to another active registration.
      const registrationsSnap = await getDocs(collection(db, 'Registrations'));
      const conflictingAssignments: string[] = [];

      registrationsSnap.forEach((registrationDoc) => {
        if (manualRegistrationId && registrationDoc.id === manualRegistrationId) return;

        const data = registrationDoc.data();
        if (data.manualDraft === true) return;

        const status = String(data.status || '').toLowerCase();
        if (status === 'cancelled' || status === 'canceled') return;

        const assignedAccommodation = String(
          data.financial?.accommodationFees?.assignedAccommodation || ''
        ).trim().toLowerCase();

        if (!assignedAccommodation || assignedAccommodation !== String(match.placeCode || '').trim().toLowerCase()) {
          return;
        }

        const camperName =
          data.registrationInfo?.primaryCamperName ||
          `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
          'Unknown Camper';

        conflictingAssignments.push(camperName);
      });

      if (conflictingAssignments.length > 0) {
        const assignedToCamperText =
          conflictingAssignments.length === 1
            ? conflictingAssignments[0]
            : `${conflictingAssignments[0]} and ${conflictingAssignments.length - 1} other registration${conflictingAssignments.length > 2 ? 's' : ''}`;

        const confirmAssignment = window.confirm(
          `Accommodation "${match.placeCode}" is already assigned to ${assignedToCamperText}. Do you want to continue to assign it to this camper as well?`
        );

        if (!confirmAssignment) {
          return;
        }
      }
    } catch (error) {
      console.error('Error checking accommodation conflicts:', error);
      alert('Could not verify whether this accommodation is already assigned. Please try again.');
      return;
    }

    setAssignedAccommodationCode(match.placeCode);
    setAccommodationAssignmentInput(match.placeCode);
    setAssignedAccommodation({
      placeCode: match.placeCode,
      placeName: match.placeName || '',
      type: match.type || '',
      priceCode: match.priceCode || ''
    });
  };

  const handleOpenCreateCamperPage = () => {
    navigate(`/admin/campers/new?returnTo=${encodeURIComponent(manualRegistrationPath)}`);
  };

  const buildRegistrationPayload = (primaryCamperId: string) => {
    const resolvedPrimaryEmail =
      primaryEmail.trim().toLowerCase() ||
      (primaryMode === 'existing' ? String(selectedExistingCamper?.email || '').trim().toLowerCase() : '');
    const resolvedPrimaryPhone =
      primaryPhoneAtCamp.trim() ||
      (primaryMode === 'existing'
        ? String(selectedExistingCamper?.HomePhone || selectedExistingCamper?.MobilePhone || '').trim()
        : '');
    const resolvedPrimaryName =
      primaryDisplayName ||
      `${primaryMode === 'existing' ? selectedExistingCamper?.FirstName || '' : newFirstName} ${primaryMode === 'existing' ? selectedExistingCamper?.LastName || '' : newLastName}`.trim();
    const resolvedAccommodationCode =
      String(assignedAccommodation?.placeCode || selectedAccommodation?.placeCode || assignedAccommodationCode || '').trim();
    const resolvedAccommodationName =
      String(assignedAccommodation?.placeName || selectedAccommodation?.placeName || '').trim();
    const resolvedAccommodationPriceCode =
      String(assignedAccommodation?.priceCode || selectedAccommodation?.priceCode || '').trim();
    const resolvedAccommodationType = resolvedAccommodationCode;

    const selectedRows = attendingCampers.filter((camper) => camper.selected);
    const camperRowsForSave = selectedRows.length > 0 ? selectedRows : attendingCampers.filter((camper) => camper.isPrimary);
    const campers = camperRowsForSave
      .map((camper) => ({
        firstName: camper.firstName,
        lastName: camper.lastName,
        age: camper.age,
        YouthChildrenCategory: camper.age === AgeCategory.YouthChildren ? (camper.youthChildrenCategory || null) : null,
        nights: camper.nights,
        programInterests: camper.programInterests || emptyProgramInterests()
      }));

    return {
      registrationInfo: {
        email: resolvedPrimaryEmail,
        phoneAtCamp: resolvedPrimaryPhone,
        phone: resolvedPrimaryPhone,
        primaryCamperName: resolvedPrimaryName,
        primaryCamperId,
        submittedAt: serverTimestamp()
      },
      accommodation: {
        policy: registrationType,
        firstChoice: resolvedAccommodationCode,
        secondChoice: '',
        notes: accommodationNotes.trim()
      },
      campDates: { arrivalDate, departureDate },
      attendingCampers: campers.length > 0 ? campers : [{
        firstName: primaryMode === 'existing' ? selectedExistingCamper?.FirstName || '' : newFirstName.trim(),
        lastName: primaryMode === 'existing' ? selectedExistingCamper?.LastName || '' : newLastName.trim(),
        age: AgeCategory.Adult,
        nights,
        programInterests: emptyProgramInterests()
      }],
      financial: {
        accommodationFees: {
          assignedAccommodation: resolvedAccommodationCode,
          placeName: resolvedAccommodationName,
          priceCode: resolvedAccommodationPriceCode,
          accommodationType: resolvedAccommodationType,
          accommodationFee: accommodationFeeEstimate,
          numberOfNights: nights,
          notes: accommodationNotes.trim()
        },
        registrationFees: registrationFeeSummary,
        otherFees: {
          meals: {
            fullCampPassesAdult: { quantity: 0, unitPrice: 0, total: 0 },
            fullCampPassesChild: { quantity: 0, unitPrice: 0, total: 0 },
            dailyPassesAdult: { quantity: 0, unitPrice: 0, total: 0 },
            dailyPassesChild: { quantity: 0, unitPrice: 0, total: 0 },
            totalMealCost: 0
          },
          fridge: { requested: fridgeRentalRequested, cost: fridgeCost },
          donations: { amount: donationCost, note: '' },
          customFee: { amount: customOtherFeesCost, note: otherFeesNote },
          totalOtherFees: otherBaseSubtotal
        },
        overrides: {
          accommodation: { ...overrides.accommodation, enabled: showOverrides.accommodation },
          registration: { ...overrides.registration, enabled: showOverrides.registration },
          mealFees: { ...overrides.mealFees, enabled: showOverrides.mealFees },
          otherFees: { ...overrides.otherFees, enabled: showOverrides.otherFees }
        }
      },
      status: registrationStatus,
      NewCamper: primaryMode === 'new',
      year: campSettings?.year || new Date().getFullYear(),
      paymentHistory: []
    };
  };

  useEffect(() => {
    if (!manualRegistrationId || loading || submitting) return;

    const syncDraft = async () => {
      try {
        const primaryCamperId = primaryMode === 'existing' ? (selectedExistingCamper?.id || '') : '';
        await updateDoc(doc(db, 'Registrations', manualRegistrationId), {
          ...buildRegistrationPayload(primaryCamperId),
          manualDraft: true,
          updatedAt: serverTimestamp()
        } as any);
      } catch (error) {
        console.error('Failed to sync manual registration draft:', error);
      }
    };

    syncDraft();
  }, [
    manualRegistrationId,
    loading,
    submitting,
    primaryMode,
    selectedExistingCamper?.id,
    primaryEmail,
    primaryPhoneAtCamp,
    primaryDisplayName,
    registrationType,
    registrationStatus,
    arrivalDate,
    departureDate,
    selectedAccommodation?.placeCode,
    assignedAccommodation?.placeCode,
    assignedAccommodation?.placeName,
    assignedAccommodation?.priceCode,
    assignedAccommodation?.type,
    assignedAccommodationCode,
    accommodationNotes,
    attendingCampers,
    accommodationFeeEstimate,
    registrationFeeSummary,
    fridgeRentalRequested,
    fridgeCost,
    donationCost,
    customOtherFeesCost,
    otherFeesNote,
    otherBaseSubtotal,
    overrides,
    showOverrides,
    totalPayments
  ]);

  const submitManualRegistration = async () => {
    const primaryFirstName = primaryMode === 'existing' ? selectedExistingCamper?.FirstName || '' : newFirstName.trim();
    const primaryLastName = primaryMode === 'existing' ? selectedExistingCamper?.LastName || '' : newLastName.trim();
    const resolvedPrimaryEmail =
      primaryEmail.trim().toLowerCase() ||
      (primaryMode === 'existing' ? String(selectedExistingCamper?.email || '').trim().toLowerCase() : '');
    const resolvedPrimaryPhone =
      primaryPhoneAtCamp.trim() ||
      (primaryMode === 'existing'
        ? String(selectedExistingCamper?.HomePhone || selectedExistingCamper?.MobilePhone || '').trim()
        : '');

    if (!primaryFirstName || !primaryLastName || !resolvedPrimaryEmail || !resolvedPrimaryPhone) {
      alert('Primary camper first name, last name, email, and phone while at camp are required.');
      return;
    }
    if (!arrivalDate || !departureDate || nights <= 0) {
      alert('Please provide valid arrival and departure dates.');
      return;
    }
    if (!assignedAccommodationCode.trim()) {
      alert('Please select an assigned accommodation.');
      return;
    }
    if (!attendingCampers.some((camper) => camper.selected)) {
      alert('Please select at least one attending camper.');
      return;
    }

    setSubmitting(true);
    try {
      let primaryCamperId = selectedExistingCamper?.id || '';
      if (primaryMode === 'new') {
        const newCamperRef = await addDoc(collection(db, 'primaryCampers'), {
          FirstName: primaryFirstName,
          LastName: primaryLastName,
          Age: AgeCategory.Adult,
          email: resolvedPrimaryEmail,
          HomePhone: resolvedPrimaryPhone,
          MobilePhone: '',
          Street: '',
          Town: '',
          Province: '',
          PostalCode: '',
          LicensePlate: '',
          dependants: [],
          notes: [],
          par: 0,
          loginExists: false,
          authUid: null,
          deleted: false,
          createdDate: serverTimestamp(),
          lastModifiedDate: serverTimestamp()
        });
        primaryCamperId = newCamperRef.id;
      }

      const registrationPayload = buildRegistrationPayload(primaryCamperId);

      if (manualRegistrationId) {
        await updateDoc(doc(db, 'Registrations', manualRegistrationId), {
          ...(registrationPayload as any),
          manualDraft: false,
          updatedAt: serverTimestamp()
        });
      } else {
        const createdRegistration = await addDoc(collection(db, 'Registrations'), {
          ...(registrationPayload as any),
          manualDraft: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setManualRegistrationId(createdRegistration.id);
      }

      if (primaryCamperId) {
        const primaryCamperRef = doc(db, 'primaryCampers', primaryCamperId);
        const primaryCamperSnap = await getDoc(primaryCamperRef);
        if (primaryCamperSnap.exists()) {
          const primaryCamperData = primaryCamperSnap.data() as any;
          const currentYear = campSettings?.year || new Date().getFullYear();
          const existingHistory = Array.isArray(primaryCamperData?.accommodationHistory)
            ? primaryCamperData.accommodationHistory
            : [];
          const assignedCodeForHistory =
            (registrationPayload as any)?.financial?.accommodationFees?.assignedAccommodation || 'No History';
          const updatedHistory = [
            { year: currentYear, placeCode: assignedCodeForHistory || 'No History' },
            ...existingHistory.filter((entry: any) => Number(entry?.year) !== Number(currentYear))
          ].slice(0, 8);

          await updateDoc(primaryCamperRef, {
            par: parBalance,
            accommodationHistory: updatedHistory,
            lastModifiedDate: serverTimestamp()
          } as any);
        }
      }

      alert('Manual registration submitted successfully. No emails were sent.');
      navigate('/admin/assignments');
    } catch (error) {
      console.error('Failed to submit manual registration:', error);
      alert('Failed to submit manual registration.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Manual Accommodation Registration</h2>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/admin/assignments')}>
          Back to Assignments
        </button>
      </div>

      <div className="row">
        <div className="col-md-6">
      {/* Left column cards: primary workflow cards */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
            Primary Contact
          </h6>
          <div className="mb-2">
            <label className="form-check form-check-inline">
              <input className="form-check-input" type="radio" checked={primaryMode === 'existing'} onChange={() => setPrimaryMode('existing')} />
              <span className="form-check-label">Select Existing Camper</span>
            </label>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm ms-2"
              onClick={handleOpenCreateCamperPage}
            >
              Create New Camper
            </button>
          </div>

          {primaryMode === 'existing' ? (
            <>
              <input
                className="form-control mb-2"
                placeholder="Search by name or email"
                value={camperSearch}
                onChange={(event) => setCamperSearch(event.target.value)}
              />

              {camperSearch.trim() && (
                <div className="border rounded mb-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  {filteredCampers.length > 0 ? (
                    filteredCampers.map((camper) => (
                      <button
                        key={camper.id}
                        type="button"
                        className="btn btn-link text-start w-100 text-decoration-none border-bottom"
                        style={{ color: 'inherit' }}
                        onClick={() => {
                          skipNextCamperAutoPopulateRef.current = false;
                          setSelectedExistingCamperId(camper.id);
                          setCamperSearch(`${camper.FirstName} ${camper.LastName}`);
                          setAttendingCampers(buildAttendingCampersForExistingCamper(camper, nights || 0));
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {camper.FirstName} {camper.LastName}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                          {camper.email || 'No email'} | {camper.MobilePhone || camper.HomePhone || 'No phone'}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-2 text-muted">No campers found matching "{camperSearch}"</div>
                  )}
                </div>
              )}

            </>
          ) : (
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <input className="form-control" placeholder="First Name *" value={newFirstName} onChange={(event) => setNewFirstName(event.target.value)} />
              </div>
              <div className="col-md-6">
                <input className="form-control" placeholder="Last Name *" value={newLastName} onChange={(event) => setNewLastName(event.target.value)} />
              </div>
            </div>
          )}

          <div className="row" style={{ marginBottom: '-5px' }}>
            <div className="col-4"><strong>Name:</strong></div>
            <div className="col-8">{primaryDisplayName || '-'}</div>
          </div>
          <div className="row" style={{ marginBottom: '-5px' }}>
            <div className="col-4"><strong>Email:</strong></div>
            <div className="col-8">
              {primaryMode === 'existing' ? (primaryEmail || '-') : (
                <input
                  className="form-control form-control-sm"
                  placeholder="Email *"
                  value={primaryEmail}
                  onChange={(event) => setPrimaryEmail(event.target.value)}
                />
              )}
            </div>
          </div>
          <div className="row" style={{ marginBottom: '-5px' }}>
            <div className="col-4"><strong>Par:</strong></div>
            <div className="col-8">${Number(parBalance || 0).toFixed(2)}</div>
          </div>
          <div className="row" style={{ marginBottom: '-5px' }}>
            <div className="col-4"><strong>Registration Type:</strong></div>
            <div className="col-8">
              <select
                className="form-select form-select-sm"
                value={registrationType}
                onChange={(event) => setRegistrationType(event.target.value)}
              >
                <option value="program-staff">1. Program Staff</option>
                <option value="returning-same-spot">2. Returning Same Spot</option>
                <option value="returning-no-fixed-address">3. Returning No Fixed Address</option>
                <option value="returning-different-accommodations">4. Returning Different Accommodations</option>
                <option value="new-camper">5. New Camper</option>
                <option value="private-cottage-owner">6. Private Cottage Owner</option>
              </select>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-4"><strong>Status:</strong></div>
            <div className="col-8">
              <select
                className="form-select form-select-sm"
                value={registrationStatus}
                style={{
                  backgroundColor: registrationStatus === 'Assigned' ? '#d4edda' :
                    registrationStatus === 'Pending' ? '#fff3cd' :
                    registrationStatus === 'Hold' ? '#d1ecf1' : 'white',
                  color: registrationStatus === 'Assigned' ? '#155724' :
                    registrationStatus === 'Pending' ? '#856404' :
                    registrationStatus === 'Hold' ? '#0c5460' : 'black'
                }}
                onChange={(event) => setRegistrationStatus(event.target.value)}
              >
                <option value="Assigned">Assigned</option>
                <option value="Pending">Pending</option>
                <option value="Hold">Hold</option>
              </select>
            </div>
          </div>

          <div className="row g-2">
            <div className="col-md-6">
              <input className="form-control" placeholder="Phone While At Camp *" value={primaryPhoneAtCamp} onChange={(event) => setPrimaryPhoneAtCamp(event.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-0" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
            Stay Details
          </h6>
          <small className="text-muted d-block mb-2">Camp Dates: {campDateRange}</small>
          <div className="row g-2">
            <div className="col-md-6">
              <input
                type="date"
                className="form-control"
                value={arrivalDate}
                onChange={(event) => setArrivalDate(event.target.value)}
                min={campStartMinDate || undefined}
                max={campEndMaxDate || undefined}
              />
              {arrivalDate && (
                <small className="text-muted d-block mt-1">{formatReadableDate(arrivalDate)}</small>
              )}
            </div>
            <div className="col-md-6">
              <input
                type="date"
                className="form-control"
                value={departureDate}
                onChange={(event) => setDepartureDate(event.target.value)}
                min={arrivalDate || campStartMinDate || undefined}
                max={campEndMaxDate || undefined}
              />
              {departureDate && (
                <small className="text-muted d-block mt-1">{formatReadableDate(departureDate)}</small>
              )}
            </div>
          </div>
          <div className="text-muted mt-2">Nights: {nights}</div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
            Accommodation Assignment
          </h6>
          <div className="mb-3">
            <label className="form-label">Assignment</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Enter accommodation assignment (e.g., c116)"
                value={accommodationAssignmentInput}
                onChange={(event) => setAccommodationAssignmentInput(event.target.value)}
                style={{ minWidth: '300px' }}
              />
              <button
                type="button"
                className="btn btn-primary btn-lg"
                disabled={!accommodationAssignmentInput.trim()}
                onClick={handleAssignAccommodation}
              >
                Assign Accommodation
              </button>
            </div>
          </div>

          {(assignedAccommodation?.placeCode || selectedAccommodation?.placeCode) && (
            <div className="alert alert-success">
              <strong>Assigned Accommodation:</strong><br />
              <strong>Place Code:</strong> {assignedAccommodation?.placeCode || selectedAccommodation?.placeCode || 'N/A'}<br />
              <strong>Place Name:</strong> {assignedAccommodation?.placeName || selectedAccommodation?.placeName || 'N/A'}<br />
              <strong>Price Code:</strong> {assignedAccommodation?.priceCode || selectedAccommodation?.priceCode || 'N/A'}
            </div>
          )}

          <textarea
            className="form-control"
            rows={3}
            placeholder="Registration Accommodation Notes"
            value={accommodationNotes}
            onChange={(event) => setAccommodationNotes(event.target.value)}
          />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title mb-0" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
              Attending Campers
            </h6>
          </div>

          {attendingCampers.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>SELECT</th>
                    <th>NAME</th>
                    <th>CATEGORY</th>
                    <th>NIGHTS</th>
                    <th className="text-center">NURSERY</th>
                    <th className="text-center">VBS</th>
                    <th className="text-center">YOUTH</th>
                    <th className="text-center">CRUISE</th>
                    <th className="text-center">VOLUNTEER</th>
                  </tr>
                </thead>
                <tbody>
                  {attendingCampers.map((camper, index) => (
                    <tr key={`${camper.firstName}-${camper.lastName}-${index}`}>
                      <td>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.selected}
                          disabled={camper.isPrimary}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index ? { ...row, selected: checked } : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td>{`${camper.firstName} ${camper.lastName}`.trim()}</td>
                      <td style={{ minWidth: '190px' }}>
                        <select
                          className="form-select form-select-sm"
                          value={camper.age}
                          onChange={(event) => {
                            const selectedAge = event.target.value as AgeCategory;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? {
                                      ...row,
                                      age: selectedAge,
                                      youthChildrenCategory: selectedAge === AgeCategory.YouthChildren
                                        ? (row.youthChildrenCategory || YouthChildrenSubCategory.Nursery)
                                        : undefined
                                    }
                                  : row
                              )
                            );
                          }}
                        >
                          <option value={AgeCategory.Adult}>Adult</option>
                          <option value={AgeCategory.AdultStudent}>Adult Student</option>
                          <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                        </select>
                        {camper.age === AgeCategory.YouthChildren && (
                          <select
                            className="form-select form-select-sm mt-1"
                            value={camper.youthChildrenCategory || ''}
                            onChange={(event) => {
                              const subCategory = event.target.value as YouthChildrenSubCategory;
                              setAttendingCampers((prev) =>
                                prev.map((row, rowIndex) =>
                                  rowIndex === index
                                    ? { ...row, youthChildrenCategory: subCategory }
                                    : row
                                )
                              );
                            }}
                          >
                            <option value="">Select Grade Level</option>
                            <option value={YouthChildrenSubCategory.Nursery}>{YouthChildrenSubCategory.Nursery}</option>
                            <option value={YouthChildrenSubCategory.PreSchool}>{YouthChildrenSubCategory.PreSchool}</option>
                            <option value={YouthChildrenSubCategory.GradeP5}>{YouthChildrenSubCategory.GradeP5}</option>
                            <option value={YouthChildrenSubCategory.Grade68}>{YouthChildrenSubCategory.Grade68}</option>
                            <option value={YouthChildrenSubCategory.Grade912}>{YouthChildrenSubCategory.Grade912}</option>
                          </select>
                        )}
                      </td>
                      <td>{getNightsDisplayText(camper.nights || 0)}</td>
                      <td className="text-center align-middle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.programInterests?.nurseryProgram}
                          disabled={!camper.selected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, programInterests: { ...(row.programInterests || emptyProgramInterests()), nurseryProgram: checked } }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td className="text-center align-middle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.programInterests?.vbsProgram}
                          disabled={!camper.selected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, programInterests: { ...(row.programInterests || emptyProgramInterests()), vbsProgram: checked } }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td className="text-center align-middle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.programInterests?.youthGroup}
                          disabled={!camper.selected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, programInterests: { ...(row.programInterests || emptyProgramInterests()), youthGroup: checked } }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td className="text-center align-middle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.programInterests?.cruiseProgram}
                          disabled={!camper.selected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, programInterests: { ...(row.programInterests || emptyProgramInterests()), cruiseProgram: checked } }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                      <td className="text-center align-middle">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!camper.programInterests?.volunteerInterest}
                          disabled={!camper.selected}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setAttendingCampers((prev) =>
                              prev.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, programInterests: { ...(row.programInterests || emptyProgramInterests()), volunteerInterest: checked } }
                                  : row
                              )
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">No attending campers available for the selected primary camper.</p>
          )}
        </div>
      </div>

      <StayHistoryCard registration={stayHistoryRegistration} />
        </div>
        <div className="col-md-6">
      {/* Right column cards: summary/support cards */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
            Financial Summary
          </h6>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}>Description</th>
                  <th style={{ border: 'none', padding: '0.25rem 0', textAlign: 'right' }}>Amount</th>
                  <th style={{ border: 'none', padding: '0.25rem 0', textAlign: 'right' }}>Subtotal</th>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}>Notes</th>
                </tr>
                <tr>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                  <th style={{ border: 'none', padding: '0.25rem 0' }}><hr style={{ margin: '0.5rem 0' }} /></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Accommodation Fees</strong></td>
                  <td />
                  <td />
                  <td />
                </tr>
                <tr>
                  <td>
                    {(assignedAccommodation?.placeCode || selectedAccommodation?.placeCode)
                      ? `${assignedAccommodation?.placeName || selectedAccommodation?.placeName || assignedAccommodation?.placeCode || selectedAccommodation?.placeCode} (${getNightsDisplayText(nights)})`
                      : 'No accommodation assigned'}
                  </td>
                  <td style={{ textAlign: 'right' }}>${accommodationFeeEstimate.toFixed(2)}</td>
                  <td />
                  <td />
                </tr>
                {showOverrides.accommodation && (
                  <tr>
                    <td>Accommodation Override</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={overrides.accommodation.amount}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            accommodation: {
                              ...prev.accommodation,
                              amount: Number(event.target.value) || 0
                            }
                          }))
                        }
                      />
                    </td>
                    <td />
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Override note"
                        value={overrides.accommodation.notes}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            accommodation: {
                              ...prev.accommodation,
                              notes: event.target.value
                            }
                          }))
                        }
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Subtotal Accommodation</strong></td>
                  <td />
                  <td style={{ textAlign: 'right' }}><strong>${accommodationSubtotal.toFixed(2)}</strong></td>
                  <td>
                    <label className="form-check-label" style={{ fontSize: '0.85rem' }}>
                      <input
                        className="form-check-input me-1"
                        type="checkbox"
                        checked={showOverrides.accommodation}
                        onChange={(event) =>
                          setShowOverrides((prev) => ({ ...prev, accommodation: event.target.checked }))
                        }
                      />
                      Override
                    </label>
                  </td>
                </tr>
                <tr>
                  <td><strong>Registration Fees</strong></td>
                  <td />
                  <td />
                  <td />
                </tr>
                {selectedCampers.map((camper, index) => (
                  <tr key={`${camper.firstName}-${camper.lastName}-${index}`}>
                    <td>
                      {camper.firstName} {camper.lastName} ({camper.age}, {getNightsDisplayText(camper.nights || 0)})
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      ${calculateCamperRegistrationFee(camper, campSettings?.rates?.registration || {}).toFixed(2)}
                    </td>
                    <td />
                    <td />
                  </tr>
                ))}
                {showOverrides.registration && (
                  <tr>
                    <td>Registration Override</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={overrides.registration.amount}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            registration: {
                              ...prev.registration,
                              amount: Number(event.target.value) || 0
                            }
                          }))
                        }
                      />
                    </td>
                    <td />
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Override note"
                        value={overrides.registration.notes}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            registration: {
                              ...prev.registration,
                              notes: event.target.value
                            }
                          }))
                        }
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Subtotal Registration</strong></td>
                  <td />
                  <td style={{ textAlign: 'right' }}><strong>${registrationSubtotal.toFixed(2)}</strong></td>
                  <td>
                    <label className="form-check-label" style={{ fontSize: '0.85rem' }}>
                      <input
                        className="form-check-input me-1"
                        type="checkbox"
                        checked={showOverrides.registration}
                        onChange={(event) =>
                          setShowOverrides((prev) => ({ ...prev, registration: event.target.checked }))
                        }
                      />
                      Override
                    </label>
                  </td>
                </tr>
                <tr>
                  <td><strong>Meal Fees</strong></td>
                  <td style={{ textAlign: 'right' }}>$0.00</td>
                  <td style={{ textAlign: 'right' }}><strong>${mealFeesSubtotal.toFixed(2)}</strong></td>
                  <td>
                    <label className="form-check-label" style={{ fontSize: '0.85rem' }}>
                      <input
                        className="form-check-input me-1"
                        type="checkbox"
                        checked={showOverrides.mealFees}
                        onChange={(event) =>
                          setShowOverrides((prev) => ({ ...prev, mealFees: event.target.checked }))
                        }
                      />
                      Override
                    </label>
                  </td>
                </tr>
                {showOverrides.mealFees && (
                  <tr>
                    <td>Meal Fees Override</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={overrides.mealFees.amount}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            mealFees: {
                              ...prev.mealFees,
                              amount: Number(event.target.value) || 0
                            }
                          }))
                        }
                      />
                    </td>
                    <td />
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Override note"
                        value={overrides.mealFees.notes}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            mealFees: {
                              ...prev.mealFees,
                              notes: event.target.value
                            }
                          }))
                        }
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td>Fridge Rental</td>
                  <td style={{ textAlign: 'right' }}>${fridgeCost.toFixed(2)}</td>
                  <td />
                  <td>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={fridgeRentalRequested}
                      onChange={(event) => setFridgeRentalRequested(event.target.checked)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>Donation</td>
                  <td style={{ textAlign: 'right' }}>
                    {donationEnabled ? (
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={donationAmount}
                        onChange={(event) => setDonationAmount(Number(event.target.value) || 0)}
                      />
                    ) : '$0.00'}
                  </td>
                  <td />
                  <td>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={donationEnabled}
                      onChange={(event) => setDonationEnabled(event.target.checked)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>Other Fees</td>
                  <td style={{ textAlign: 'right' }}>
                    {otherFeesEnabled ? (
                      <div>
                        <input
                          type="number"
                          className="form-control form-control-sm mb-1"
                          value={otherFeesAmount}
                          onChange={(event) => setOtherFeesAmount(Number(event.target.value) || 0)}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Other fees note"
                          value={otherFeesNote}
                          onChange={(event) => setOtherFeesNote(event.target.value)}
                        />
                      </div>
                    ) : '$0.00'}
                  </td>
                  <td />
                  <td>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={otherFeesEnabled}
                      onChange={(event) => setOtherFeesEnabled(event.target.checked)}
                    />
                  </td>
                </tr>
                <tr>
                  <td><strong>Other Fees</strong></td>
                  <td />
                  <td style={{ textAlign: 'right' }}><strong>${otherFeesSubtotal.toFixed(2)}</strong></td>
                  <td>
                    <label className="form-check-label" style={{ fontSize: '0.85rem' }}>
                      <input
                        className="form-check-input me-1"
                        type="checkbox"
                        checked={showOverrides.otherFees}
                        onChange={(event) =>
                          setShowOverrides((prev) => ({ ...prev, otherFees: event.target.checked }))
                        }
                      />
                      Override
                    </label>
                  </td>
                </tr>
                {showOverrides.otherFees && (
                  <tr>
                    <td>Other Fees Override</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={overrides.otherFees.amount}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            otherFees: {
                              ...prev.otherFees,
                              amount: Number(event.target.value) || 0
                            }
                          }))
                        }
                      />
                    </td>
                    <td />
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Override note"
                        value={overrides.otherFees.notes}
                        onChange={(event) =>
                          setOverrides((prev) => ({
                            ...prev,
                            otherFees: {
                              ...prev.otherFees,
                              notes: event.target.value
                            }
                          }))
                        }
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Total</strong></td>
                  <td />
                  <td style={{ textAlign: 'right' }}><strong>${estimatedGrandTotal.toFixed(2)}</strong></td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="d-grid mb-3">
        <button
          type="button"
          className="btn btn-success btn-lg"
          disabled={submitting}
          onClick={submitManualRegistration}
        >
          {submitting ? 'Submitting...' : 'Submit Manual Registration'}
        </button>
      </div>

      {/* Future right-side cards should be added here */}
        </div>
      </div>
    </div>
  );
};

export default ManualRegistrationPage;
